/**
 * Props contract for the clip-v2 social compositions (see ../CLIP_V2_CONTRACT.md).
 *
 * Same rules as v1: everything on screen comes from validated props (numbers are
 * engine output, copy is plain text), the clip lives under `inputProps.clip`, and
 * any invalid or missing required field throws, failing the render.
 */
import { z } from "zod";
import {
  amountOrUnlimited,
  beatSchema,
  brandSchema,
  emphasisSchema,
  finite,
  httpUrl,
  payoffSeries,
  wordTimingSchema,
} from "../scenarioExplainerSchema.js";
import { pnlAtPrice } from "../scenarioExplainerTimeline.js";

export const CLIP_V2_FORMATS = ["why-it-lost", "compare-two", "change-one", "explainer"];
export const ASSUMPTION_KINDS = ["price", "time", "volatility"];
/** Where the numbers come from: "modeled" (engine projection) or "observed" (today's market quotes). */
export const DATA_LABELS = ["modeled", "observed"];

export const HOOK_MAX_CHARS = 70;
export const LESSON_MAX_WORDS = 8;
/** Longest narration the clip will stretch to (Shorts hard limit is 60 s). */
export const MAX_VOICEOVER_SECONDS = 52;
export const MAX_PRESENTER_SECONDS = 10;
export const DEFAULT_CTA_TEXT = "Try it with your numbers";

const wordCount = (text) => String(text || "").split(/\s+/).filter(Boolean).length;

/** Visible hook text: `**markup**` removed. */
export function stripHookMarkup(text) {
  return String(text).replace(/\*\*/g, "");
}

const hookSchema = z
  .string()
  .trim()
  .min(1, "hook text is required")
  .superRefine((value, ctx) => {
    const markers = (value.match(/\*\*/g) || []).length;
    if (markers % 2 !== 0) {
      ctx.addIssue({ code: "custom", message: "unbalanced **emphasis** markup" });
    }
    const visible = stripHookMarkup(value).trim();
    if (!visible) ctx.addIssue({ code: "custom", message: "hook text is required" });
    if (visible.length > HOOK_MAX_CHARS) {
      ctx.addIssue({ code: "custom", message: `hook must be <= ${HOOK_MAX_CHARS} visible characters` });
    }
  });

const ticker = z
  .string()
  .trim()
  .min(1, "ticker is required")
  .max(12)
  .transform((value) => value.toUpperCase());

const strategySchema = z.object({
  label: z.string().trim().min(1, "strategy.label is required").max(32),
  /** One line, e.g. "Buy 1 AAPL 230 call · 30 days · $9.68". */
  legs: z.string().trim().min(1, "strategy.legs is required").max(90),
});

const numbersSchema = z.object({
  /** Worst-case P&L in dollars; sign is ignored (always shown as a loss). */
  maxLoss: amountOrUnlimited,
  maxGain: amountOrUnlimited,
  breakevens: z.array(finite.positive()).min(1, "at least one breakeven is required").max(4),
  /** > 0 = net debit paid, < 0 = net credit received (dollars). */
  netDebitCredit: finite,
});

const payoffSchema = z.object({
  expiration: payoffSeries,
  /** Modeled P&L curve at the scenario date (before expiration), optional. */
  tPlus0: payoffSeries.nullable().default(null),
});

const lessonSchema = z.object({
  text: z
    .string()
    .trim()
    .min(1, "lesson.text is required")
    .max(56)
    .refine((v) => wordCount(v) <= LESSON_MAX_WORDS, `lesson.text must be <= ${LESSON_MAX_WORDS} words`),
  /** Big number on the payoff card; derived from the format's data when omitted. */
  number: emphasisSchema.nullable().default(null),
});

const ctaSchema = z
  .object({
    text: z.string().trim().min(1).max(40).default(DEFAULT_CTA_TEXT),
    url: z
      .string()
      .trim()
      .min(1, "cta.url is required")
      .max(48)
      .transform((value) => value.replace(/^https?:\/\//i, "").replace(/\/+$/, "")),
  });

const scriptLine = z.string().trim().min(1).max(110);
/** Optional caption/narration copy per scene (used when there is no voiceover word timing). */
const scriptSchema = z
  .object({
    hook: scriptLine.nullable().default(null),
    setup: scriptLine.nullable().default(null),
    twist: z.array(scriptLine).max(8).nullable().default(null),
    payoff: scriptLine.nullable().default(null),
    cta: scriptLine.nullable().default(null),
  })
  .prefault({});

const voiceoverSchema = z.object({
  audioUrl: httpUrl,
  /** Word timings (ms from video start). Drive captions and scene stretching. */
  words: z.array(wordTimingSchema).max(2000).default([]),
  durationSeconds: finite.positive().max(MAX_VOICEOVER_SECONDS).nullable().default(null),
});

const musicSchema = z.object({
  audioUrl: httpUrl,
  volume: finite.min(0).max(1).default(0.12),
  duckUnderVoice: z.boolean().default(true),
});

const sfxSchema = z.object({
  whooshUrl: httpUrl.nullable().default(null),
  hitUrl: httpUrl.nullable().default(null),
});

const presenterSchema = z.object({
  videoUrl: httpUrl,
  startSec: finite.nonnegative(),
  endSec: finite.positive(),
  placement: z.enum(["full", "pip"]).default("pip"),
  /** Mute the clip's own audio (e.g. when a voiceover covers it). */
  muted: z.boolean().default(false),
});

const commonShape = {
  ticker,
  /** Underlying price at the start of the story. */
  spot: finite.positive(),
  hooks: z.array(hookSchema).min(1, "1-3 hooks are required").max(3, "1-3 hooks are required"),
  hookIndex: z.number().int().min(0).max(2).default(0),
  lesson: lessonSchema,
  cta: ctaSchema,
  dataAsOf: z.string().trim().min(1, "dataAsOf is required").max(48),
  /** Tag next to the data-as-of line: "Modeled" (default) or "Market data" (observed). */
  dataLabel: z.enum(DATA_LABELS).default("modeled"),
  disclaimer: z.string().trim().min(1, "disclaimer text is required").max(320),
  script: scriptSchema,
  voiceover: voiceoverSchema.nullable().default(null),
  music: musicSchema.nullable().default(null),
  sfx: sfxSchema.nullable().default(null),
  presenter: presenterSchema.nullable().default(null),
  brand: brandSchema,
  progressBar: z.boolean().default(true),
  fps: finite.positive().nullable().default(null),
};

const whyItLostSchema = z.object({
  format: z.literal("why-it-lost"),
  ...commonShape,
  strategy: strategySchema,
  numbers: numbersSchema,
  payoff: payoffSchema,
  scenarioMove: z.object({
    fromSpot: finite.positive(),
    toSpot: finite.positive(),
    /** Calendar days between entry and the scenario. */
    days: z.number().int().min(0).max(3650),
    /** Change in implied volatility, percentage points (−19 = IV fell 19 points). */
    ivChangePct: finite.min(-100).max(500).nullable().default(null),
  }),
  /** Modeled P&L (dollars) of the position after the move. Must be a loss. */
  pnlAtMove: finite.refine((v) => v < 0, "pnlAtMove must be negative (the trade lost money)"),
});

const compareStrategySchema = z.object({
  label: z.string().trim().min(1).max(24),
  legs: z.string().trim().min(1).max(70),
  numbers: numbersSchema,
  payoff: payoffSchema,
});

const compareTwoSchema = z.object({
  format: z.literal("compare-two"),
  ...commonShape,
  /** The shared market view, e.g. "Bullish on AAPL". */
  view: z.string().trim().min(1, "view is required").max(40),
  strategies: z.tuple([compareStrategySchema, compareStrategySchema]),
  /** Short verdict line; derived from max loss when omitted. */
  verdict: z.string().trim().min(1).max(60).nullable().default(null),
});

const curveAtSchema = z.object({
  payoff: payoffSeries,
  /** Modeled P&L (dollars) at the reference price under this assumption. */
  pnl: finite,
});

const changeOneSchema = z.object({
  format: z.literal("change-one"),
  ...commonShape,
  strategy: strategySchema,
  numbers: numbersSchema,
  assumption: z.object({
    kind: z.enum(ASSUMPTION_KINDS),
    /** price: underlying $, time: days to expiration, volatility: IV % (e.g. 32). */
    from: finite.nonnegative(),
    to: finite.nonnegative(),
    label: z.string().trim().min(1).max(32).nullable().default(null),
  }),
  baseline: curveAtSchema,
  changed: curveAtSchema,
});

const explainerSchema = z.object({
  format: z.literal("explainer"),
  ...commonShape,
  strategy: strategySchema,
  numbers: numbersSchema,
  payoff: payoffSchema,
  beats: z.array(beatSchema).min(3, "needs 3-5 beats").max(5, "needs 3-5 beats"),
});

function checkSeriesSpan(series, path, ctx) {
  if (!series) return;
  const lo = series[0].underlyingPrice;
  const hi = series[series.length - 1].underlyingPrice;
  if (!(hi > lo)) ctx.addIssue({ code: "custom", path, message: "underlyingPrice values must span a range" });
}

function checkPriceInSeries(series, price, path, ctx, what) {
  const lo = series[0].underlyingPrice;
  const hi = series[series.length - 1].underlyingPrice;
  if (price < lo || price > hi) {
    ctx.addIssue({ code: "custom", path, message: `${what} (${price}) is outside the payoff price range ${lo}-${hi}` });
  }
}

/** The engine's point P&L must agree with its own curve (catches mismatched payloads). */
function checkPnlMatchesCurve(series, price, pnl, path, ctx) {
  const curve = pnlAtPrice(series, price);
  const range = Math.max(...series.map((p) => p.pnl)) - Math.min(...series.map((p) => p.pnl));
  const tolerance = Math.max(10, Math.abs(pnl) * 0.12, range * 0.03);
  if (Math.abs(curve - pnl) > tolerance) {
    ctx.addIssue({
      code: "custom",
      path,
      message: `P&L ${pnl} disagrees with its payoff curve at ${price} (${Math.round(curve)})`,
    });
  }
}

export const clipV2Schema = z
  .discriminatedUnion("format", [whyItLostSchema, compareTwoSchema, changeOneSchema, explainerSchema], {
    error: (issue) =>
      issue.code === "invalid_union" ? `format must be one of: ${CLIP_V2_FORMATS.join(", ")}` : undefined,
  })
  .superRefine((props, ctx) => {
    if (props.hookIndex >= props.hooks.length) {
      ctx.addIssue({ code: "custom", path: ["hookIndex"], message: `hookIndex must be < hooks.length (${props.hooks.length})` });
    }
    props.voiceover?.words?.forEach((word, index) => {
      if (word.endMs < word.startMs) {
        ctx.addIssue({ code: "custom", path: ["voiceover", "words", index, "endMs"], message: "endMs must be >= startMs" });
      }
      if (word.endMs > MAX_VOICEOVER_SECONDS * 1000) {
        ctx.addIssue({
          code: "custom",
          path: ["voiceover", "words", index, "endMs"],
          message: `narration must end within ${MAX_VOICEOVER_SECONDS}s`,
        });
      }
    });
    if (props.presenter) {
      const len = props.presenter.endSec - props.presenter.startSec;
      if (!(len > 0)) ctx.addIssue({ code: "custom", path: ["presenter", "endSec"], message: "endSec must be > startSec" });
      if (len > MAX_PRESENTER_SECONDS) {
        ctx.addIssue({ code: "custom", path: ["presenter", "endSec"], message: `presenter clip must be <= ${MAX_PRESENTER_SECONDS}s` });
      }
    }

    switch (props.format) {
      case "why-it-lost": {
        checkSeriesSpan(props.payoff.expiration, ["payoff", "expiration"], ctx);
        checkSeriesSpan(props.payoff.tPlus0, ["payoff", "tPlus0"], ctx);
        const { fromSpot, toSpot } = props.scenarioMove;
        if (fromSpot === toSpot) {
          ctx.addIssue({ code: "custom", path: ["scenarioMove", "toSpot"], message: "toSpot must differ from fromSpot" });
        }
        checkPriceInSeries(props.payoff.expiration, fromSpot, ["scenarioMove", "fromSpot"], ctx, "fromSpot");
        checkPriceInSeries(props.payoff.expiration, toSpot, ["scenarioMove", "toSpot"], ctx, "toSpot");
        if (props.payoff.tPlus0) {
          checkPnlMatchesCurve(props.payoff.tPlus0, toSpot, props.pnlAtMove, ["pnlAtMove"], ctx);
        }
        break;
      }
      case "compare-two":
        props.strategies.forEach((s, i) => {
          checkSeriesSpan(s.payoff.expiration, ["strategies", i, "payoff", "expiration"], ctx);
          checkSeriesSpan(s.payoff.tPlus0, ["strategies", i, "payoff", "tPlus0"], ctx);
        });
        if (props.strategies[0].numbers.maxLoss === "Unlimited" && props.strategies[1].numbers.maxLoss === "Unlimited") {
          ctx.addIssue({
            code: "custom",
            path: ["strategies"],
            message: "compare-two needs at least one strategy with a defined max loss",
          });
        }
        break;
      case "change-one": {
        checkSeriesSpan(props.baseline.payoff, ["baseline", "payoff"], ctx);
        checkSeriesSpan(props.changed.payoff, ["changed", "payoff"], ctx);
        const { kind, from, to } = props.assumption;
        if (from === to) ctx.addIssue({ code: "custom", path: ["assumption", "to"], message: "to must differ from from" });
        if (kind === "price") {
          if (!(from > 0 && to > 0)) {
            ctx.addIssue({ code: "custom", path: ["assumption"], message: "price assumptions must be > 0" });
          } else {
            checkPriceInSeries(props.baseline.payoff, from, ["assumption", "from"], ctx, "from");
            checkPriceInSeries(props.changed.payoff, to, ["assumption", "to"], ctx, "to");
            checkPnlMatchesCurve(props.baseline.payoff, from, props.baseline.pnl, ["baseline", "pnl"], ctx);
            checkPnlMatchesCurve(props.changed.payoff, to, props.changed.pnl, ["changed", "pnl"], ctx);
          }
        } else {
          checkPriceInSeries(props.baseline.payoff, props.spot, ["spot"], ctx, "spot");
          checkPnlMatchesCurve(props.baseline.payoff, props.spot, props.baseline.pnl, ["baseline", "pnl"], ctx);
          checkPnlMatchesCurve(props.changed.payoff, props.spot, props.changed.pnl, ["changed", "pnl"], ctx);
        }
        if (kind === "time" && (!Number.isInteger(from) || !Number.isInteger(to))) {
          ctx.addIssue({ code: "custom", path: ["assumption"], message: "time assumptions are whole days" });
        }
        break;
      }
      case "explainer":
        checkSeriesSpan(props.payoff.expiration, ["payoff", "expiration"], ctx);
        checkSeriesSpan(props.payoff.tPlus0, ["payoff", "tPlus0"], ctx);
        break;
      default:
        break;
    }
  });

/** Every top-level clip key across formats (used to catch flat, un-nested payloads). */
export const CLIP_V2_KEYS = Array.from(
  new Set(
    [whyItLostSchema, compareTwoSchema, changeOneSchema, explainerSchema].flatMap((s) => Object.keys(s.shape))
  )
);

function formatIssues(error) {
  return error.issues
    .map((issue) => `${issue.path.length ? issue.path.join(".") : "(root)"}: ${issue.message}`)
    .join("; ");
}

/** Validate + normalise clip props. Throws on any invalid or missing field. Idempotent. */
export function parseClipV2Props(input) {
  const result = clipV2Schema.safeParse(input ?? {});
  if (!result.success) {
    throw new Error(`ClipV2: invalid props — ${formatIssues(result.error)}`);
  }
  return result.data;
}

/**
 * Remotion shallow-merges inputProps over defaultProps, so the clip lives under a
 * single `clip` key and replaces the Studio fixture wholesale. Flat clip keys throw.
 */
export function parseClipV2InputProps(inputProps) {
  const misplaced = CLIP_V2_KEYS.filter(
    (key) => inputProps && Object.prototype.hasOwnProperty.call(inputProps, key)
  );
  if (misplaced.length) {
    throw new Error(`ClipV2: invalid props — ${misplaced.join(", ")}: must be nested under inputProps.clip`);
  }
  const clip = inputProps?.clip;
  if (!clip || typeof clip !== "object" || Array.isArray(clip)) {
    throw new Error("ClipV2: invalid props — clip: inputProps.clip object is required");
  }
  return parseClipV2Props(clip);
}
